/**
 * Client entry: fonts and the ported runebound.gg design system, the session, the query cache, the
 * router; a hand-off fragment on any page signs in first (owner decision Q11), and crashes are reported
 * to the beacon (docs/design/06 §6).
 */
import '@fontsource-variable/inter';
import '@fontsource-variable/jetbrains-mono';
import '@fontsource-variable/plus-jakarta-sans';
import './styles.css';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider } from '@tanstack/react-router';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { AuthProvider } from './auth/auth';
import { installBeacon } from './lib/beacon';
import { router } from './router';

installBeacon();

// A Runebound hand-off can land on any page (`play-handoff` appends `#token_hash=…` to its redirect):
// route it through /auth/handoff first, which signs in and then continues to that page.
if (/(^|[#&])(token_hash|th)=/.test(window.location.hash) && window.location.pathname !== '/auth/handoff') {
  const next = window.location.pathname + window.location.search;
  window.history.replaceState(null, '', `/auth/handoff?next=${encodeURIComponent(next)}${window.location.hash}`);
}

const queryClient = new QueryClient({
  defaultOptions: { queries: { retry: 1, refetchOnWindowFocus: false } },
});

const root = document.getElementById('root');
if (!root) throw new Error('Missing #root');
createRoot(root).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <RouterProvider router={router} />
      </AuthProvider>
    </QueryClientProvider>
  </StrictMode>,
);
